import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { importAction } from '@nxsys/store/slices/import/reducer/importSlice';
import { GetImportAgencyListParams } from '@nxsys/store/slices/import/types';
import { Icons } from '@nxsys/types/icons';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { IRELAND } from '@nxsys/store/constants/keys';
import { getImportAgencyColumns } from './columns';
import UploadFileModal from '@nxsys/components/common/UploadFileModal';
import { useImportAgency } from '@nxsys/hooks/api/import';
import ImportResultUploadModal from '../components/ImportResultUploadModal';
import ViewImportedFile from '../components/ViewImportedFile';
import { MASTER_TEMPLATE_FILE_PATH, downloadFile } from '@nxsys/constants/templateFiles';
import { IMPORT_SCREEN_TYPE } from '../constants';

const ImportAgencyListing = () => {
  const { messages } = useTypedIntl();

  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  // NX-5228: agency import is umbrella-only, so before Ireland joined the
  // umbrella product one UK template was correct.
  const isIreland = payrollCountry === IRELAND;
  const { importAgencyList } = useSelector(
    (state: AppState) => state.importStore,
  );
  const { loading, list, pagination, queryParams } = importAgencyList;

  const dispatch = useDispatch<AppDispatch>();

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['common.import']}
        separator="-"
        title={messages['common.agencies']}
      />
    );
  }, [messages]);

  const renderRightContent = useCallback(() => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          name={messages['common.downloadMasterTemplate']}
          onClick={() =>
            downloadFile(
              isIreland
                ? MASTER_TEMPLATE_FILE_PATH.umbrellaIreland.importAgency
                : MASTER_TEMPLATE_FILE_PATH.umbrella.importAgency,
            )
          }
          leftIcon={
            <Icons.IconDownload
              color={defaultTheme.theme.palette.primary.main}
            />
          }
        />
        <AppButton
          name={messages['common.importFile']}
          onClick={() => setOpenUploadFileModal(true)}
          leftIcon={
            <Icons.IconFileImport color={defaultTheme.theme.palette.white} />
          }
        />
      </div>
    );
  }, [messages]);

  const [searchValue, setSearchValue] = useState('');
  const [openUploadFileModal, setOpenUploadFileModal] = useState(false);
  const [openResultUploadModal, setOpenResultUploadModal] = useState(false);
  const [dataImported, setDataImported] = useState<IImportAgency | null>(null);
  const [openViewImportedFileModal, setOpenViewImportedFileModal] =
    useState(false);

  const { run: importAgency, loading: importAgencyLoading } = useImportAgency();

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetImportAgencyListParams) => {
    dispatch(importAction.getImportAgencyList(params));
  };

  const refetchData = () =>
    handleFetchData({
      ...queryParams,
    });

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const handleUploaded = useCallback(
    async (fileData: { url: string; fileName: string; fileSize: number }) => {
      const params = {
        ...fileData,
      };
      setOpenResultUploadModal(true);
      const res = await importAgency(params);
      setDataImported(res.data);
    },
    [],
  );

  const handleViewImportedFile = useCallback((data: IImportAgency) => {
    setDataImported(data);
    setOpenViewImportedFileModal(true);
  }, []);

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={importAgencyLoading}
    >
      <AppCard>
        <AppsHeader>
          <div className="px-2 w-full">
            <AppInputSearch value={searchValue} onChange={handleSearchChange} />
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            data={
              list?.map((item, index) => ({
                ...item,
                indexNumber:
                  pagination.current * pagination.pageSize + index + 1,
              })) ?? []
            }
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1000, y: 700 }}
            columns={getImportAgencyColumns({
              messages,
              onView: handleViewImportedFile,
            })}
            onChangePagination={onChangePagination}
            noDataType={!!queryParams?.search ? 'noDataFound' : 'noDataCreated'}
            addNewBtnName={messages['common.importFile']}
            addNewBtnOnClick={() => setOpenUploadFileModal(true)}
            addNewIcon={null}
          />
        </AppsContent>
      </AppCard>
      <UploadFileModal
        open={openUploadFileModal}
        onClose={() => setOpenUploadFileModal(false)}
        onUploaded={handleUploaded}
        fileType="spreadsheet"
      />
      <ImportResultUploadModal
        isOpen={openResultUploadModal}
        toggle={() => setOpenResultUploadModal(!openResultUploadModal)}
        resultData={dataImported as any}
        loading={importAgencyLoading}
        refetchData={refetchData}
        importScreenType={IMPORT_SCREEN_TYPE.AGENCY}
      />
      <ViewImportedFile
        isOpen={openViewImportedFileModal}
        toggle={() => setOpenViewImportedFileModal(!openViewImportedFileModal)}
        resultData={dataImported as any}
        importScreenType={IMPORT_SCREEN_TYPE.AGENCY}
      />
    </AppsContainer>
  );
};

export default ImportAgencyListing;
